<template>
  <div v-if="!authStore.loggedIn" class="text-center mb-3">
    <img :src="logoSrc" alt="Logo" class="img-fluid" style="max-width: 200px;" />
  </div>
  <div class="container mt-4">
    <div class="row justify-content-center">
      <div class="col-md-5">
        <div class="card shadow-sm">
          <div class="card-body">
            <h3 class="text-center mb-3 fw-semibold">Login</h3>
            <!-- Success Alert -->
            <div v-if="showSuccess" class="alert alert-success alert-dismissible fade show mb-3" role="alert">
              Successfully logged in!
              <button type="button" class="btn-close" @click="showSuccess = false" aria-label="Close"></button>
            </div>
            <!-- Error Alert -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show mb-3" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="clearError" aria-label="Close"></button>
            </div>
            <form @submit.prevent="handleLogin">
              <FormField 
                v-model="username" 
                id="username" 
                type="text" 
                placeholder="Enter your username"
                icon="fas fa-envelope"
                :disabled="isLoading"
              />
              <FormField 
                v-model="password" 
                id="password" 
                type="password" 
                placeholder="Enter your password"
                icon="fas fa-lock"
                :disabled="isLoading"
              />
              <div class="d-grid">
                <button 
                  type="submit" 
                  class="btn btn-primary btn-sm mt-2"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ isLoading ? 'Logging in...' : 'Login' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FormField from '@/components/FormField.vue';
import { useAuthStore } from '@/stores/authStore';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const username = ref('');
const password = ref('');
const logoSrc = require('@/assets/logo.webp');
const router = useRouter();
const route = useRoute();
const showSuccess = ref(false);

// Use storeToRefs to maintain reactivity for store state
const authStore = useAuthStore();
const { error, loading } = storeToRefs(authStore);

const isLoading = computed(() => loading.value);

const clearError = () => {
  error.value = null;
};

const handleLogin = async () => {
  try {
    await authStore.login(username.value, password.value);
    
    if (authStore.loggedIn) {
      // Show success message
      showSuccess.value = true;
      
      // Hide success message after 1.5 seconds
      setTimeout(() => {
        showSuccess.value = false;
        // Redirect after hiding the success message
        const redirectPath = route.query.redirect as string || '/home';
        router.push(redirectPath);
      }, 1500);
    }
  } catch (err) {
    console.error('Login failed:', err);
  }
};
</script>

<style scoped>
.card {
  border-radius: 8px;
}

.card-body {
  padding: 1.5rem;
}

h3 {
  font-size: 1.25rem;
}

.btn-sm {
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
}

.container {
  max-width: 100%;
}

.alert {
  font-size: 0.9rem;
  padding: 0.75rem;
}

.alert-dismissible .btn-close {
  padding: 0.8rem;
}

/* Add animation for alerts */
.alert {
  transition: opacity 0.3s ease-in-out;
}

.fade {
  opacity: 1;
}

.fade.show {
  opacity: 1;
}
</style>